/**
 * Shared domain types for the ARIA Training Portal.
 *
 * These mirror the FastAPI Pydantic models in src/backend/.
 * Keep in sync with API contracts: docs/architecture/api-contracts.md
 */

// ---------------------------------------------------------------------------
// Auth / User
// ---------------------------------------------------------------------------

// Legacy roles (ADR-007) plus ADR-011 five-tier roles.
export type UserRole =
  | 'admin' | 'manager' | 'agent'                                   // legacy
  | 'platform_admin' | 'tenant_admin' | 'sub_tenant_admin'
  | 'content_manager' | 'learner';                                  // ADR-011
export type UserLanguage = 'en' | 'zh-TW' | 'ms' | 'hi';
export type UserPortal = 'admin' | 'training' | 'platform' | 'both';

export type AgentLevel = 'L1' | 'L2' | 'L3' | 'L4' | 'L5';
export type CompetencyTrack = 'product' | 'sales' | 'compliance' | 'leadership';

export interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  portal: UserPortal;
  language: UserLanguage;
  agent_level?: AgentLevel;
  country?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponseData {
  access_token: string;
  token_type: 'bearer';
  expires_in: number;
  refresh_token: string;
  user: UserProfile;
}

// ---------------------------------------------------------------------------
// Generic API envelope (all ARIA REST responses use this shape)
// ---------------------------------------------------------------------------

export interface ApiResponse<T> {
  status: 'success' | 'error';
  data: T | null;
  error: ApiErrorBody | null;
}

export interface ApiErrorBody {
  code: string;
  message: string;
}

// ---------------------------------------------------------------------------
// Scenarios (GET /api/v1/scenarios)
// ---------------------------------------------------------------------------

export type ScenarioDifficulty = 'easy' | 'intermediate' | 'advanced';

export interface Scenario {
  id: string;
  title: string;
  description: string;
  difficulty: ScenarioDifficulty;
  language: string;
  product_type: string;
  is_active: boolean;
}

export interface ScenariosResponseData {
  scenarios: Scenario[];
}

// ---------------------------------------------------------------------------
// Sessions (POST /api/v1/sessions, GET /api/v1/sessions/{id})
// ---------------------------------------------------------------------------

export type SessionStatus = 'active' | 'completed' | 'abandoned';

export interface StartSessionRequest {
  scenario_id: string;
  language: UserLanguage;
}

export interface StartSessionResponseData {
  session_id: string;
  ws_url: string;
  reconnect_token: string;
  expires_at: string; // ISO-8601
}

export interface SessionDetail {
  session_id: string;
  status: SessionStatus;
  scenario: { id: string; title: string };
  language: UserLanguage;
  started_at: string; // ISO-8601
  turn_count: number;
  compliance_violation_count: number;
}

export interface EndSessionResponseData {
  session_id: string;
  status: SessionStatus;
  score_ready: boolean;
  score_url: string;
}

// ---------------------------------------------------------------------------
// Scores (GET /api/v1/sessions/{id}/score)
// ---------------------------------------------------------------------------

export interface ComplianceViolation {
  turn: number;
  rule_id: string;
  trigger_phrase: string;
  guidance: string;
}

export interface CompetencyScores {
  communication: number;
  product_knowledge: number;
  compliance: number;
  objection_handling: number;
}

export interface ScoreData {
  session_id: string;
  overall_score: number;
  passed: boolean;
  competency_scores: CompetencyScores;
  llm_feedback: string;
  compliance_violations: ComplianceViolation[];
}

// ---------------------------------------------------------------------------
// WebSocket message types (wss://.../ws/sessions/{id})
// See: docs/architecture/api-contracts.md — WebSocket Protocol
// ---------------------------------------------------------------------------

export type WsClientMessageType = 'audio_chunk' | 'end_turn' | 'feedback';
export type WsServerMessageType =
  | 'connected'
  | 'pipeline_state'
  | 'pong'
  | 'transcript_partial'
  | 'transcript_final'
  | 'audio_chunk'
  | 'compliance_violation'
  | 'session_ended'
  | 'audio_config'
  | 'objectives_progress'
  | 'error';

export interface WsObjectivesProgress {
  type: 'objectives_progress';
  data: { objectives: string[]; met: number[]; total: number };
}

export interface WsAudioConfig {
  type: 'audio_config';
  data: { stt: string; tts: string; mode: string; show_badge: boolean };
}

export interface WsConnected {
  type: 'connected';
  data: { session_id: string; mock_mode: boolean; pipeline_state: string };
}

export interface WsPipelineState {
  type: 'pipeline_state';
  data: { state: 'listening' | 'thinking' | 'speaking' | 'transcribing' | 'error' };
}

export interface WsPong {
  type: 'pong';
  data: Record<string, never>;
}

export interface WsTranscriptPartial {
  type: 'transcript_partial';
  data: { speaker: 'agent' | 'ai'; text: string };
}

export interface WsTranscriptFinal {
  type: 'transcript_final';
  data: { speaker: 'agent' | 'ai'; text: string; turn: number };
}

export interface WsAudioChunk {
  type: 'audio_chunk';
  /**
   * The pipeline sends:  { data: "<base64 PCM>", encoding: "pcm", sample_rate?: number }
   * Not a bare string — the real audio payload lives in data.data.
   */
  data: {
    data: string;       // base64-encoded PCM-16 mono
    encoding: string;   // "pcm"
    sample_rate?: number;
    mock?: boolean;
  };
}

export interface WsComplianceViolation {
  type: 'compliance_violation';
  data: {
    rule_id: string;
    severity: 'blocker' | 'warning';
    message: string;
    guidance: string;
  };
}

// ---------------------------------------------------------------------------
// Transcript turn (EPIC-06-04)
// ---------------------------------------------------------------------------

/**
 * A single turn in the session transcript.
 * `originalText` is set when auto-translation replaced the agent's typed input
 * so both the original English and the translated text can be shown (AC-2).
 */
export interface TranscriptTurn {
  turn: number;
  speaker: 'agent' | 'ai';
  text: string;
  /** Original English text before auto-translation (AC-2, AC-7). */
  originalText?: string;
}

// ---------------------------------------------------------------------------
// Compliance transcript entry (EPIC-03-03)
// ---------------------------------------------------------------------------

export interface ComplianceTurn {
  type: 'compliance';
  turnNumber: number;
  ruleId: string;
  severity: 'blocker' | 'warning';
  /** Truncated to 80 chars in the UI */
  triggerPhrase: string;
  guidance: string;
}

export interface WsSessionEnded {
  type: 'session_ended';
  data: { score_url: string };
}

export interface WsError {
  type: 'error';
  data: { code: string; message: string };
}

/** Discriminated union of all server → client WebSocket messages */
export type WsServerMessage =
  | WsConnected
  | WsPipelineState
  | WsPong
  | WsTranscriptPartial
  | WsTranscriptFinal
  | WsAudioChunk
  | WsComplianceViolation
  | WsSessionEnded
  | WsAudioConfig
  | WsObjectivesProgress
  | WsError;

// ---------------------------------------------------------------------------
// LMS — Recommendations (EPIC-08-10a/b)
// ---------------------------------------------------------------------------

export type CompetencyReasonKey =
  | 'weak_compliance'
  | 'weak_product_knowledge'
  | 'weak_communication'
  | 'weak_objection_handling';

export interface Recommendation {
  course_id: string;
  title: string;
  type: string;
  rank: number;
  reason_key: CompetencyReasonKey;
  weak_score: number;
  language: string;
  estimated_duration_mins: number;
}

export interface RecommendationsResponseData {
  courses: Recommendation[];
  computed_at: string | null;
  reason: 'insufficient_data' | null;
}

/**
 * Type guard: narrow an unknown value to a WsServerMessage.
 * Used in SessionPage to safely handle incoming WebSocket frames.
 */
export function isWsServerMessage(value: unknown): value is WsServerMessage {
  if (typeof value !== 'object' || value === null) return false;
  const msg = value as Record<string, unknown>;
  const knownTypes: WsServerMessageType[] = [
    'connected',
    'pipeline_state',
    'pong',
    'transcript_partial',
    'transcript_final',
    'audio_chunk',
    'compliance_violation',
    'session_ended',
    'audio_config',
    'objectives_progress',
    'error',
  ];
  return typeof msg['type'] === 'string' && (knownTypes as string[]).includes(msg['type']);
}
